/**
 * **Le vocabulaire des fins, en français, à UN seul endroit** ⟦A-58 · A-97 §2⟧.
 *
 * A-97 §2 le demande explicitement : l'historique « s'appuie sur ce vocabulaire, il ne le
 * duplique pas ». Ces tables vivaient dans `Palmares.tsx`, en constantes privées ; un deuxième
 * écran qui les recopie, c'est une deuxième table qui oubliera la prochaine `ended_reason` —
 * exactement l'accident d'⟦A-58⟧, où le palmarès affichait le mot anglais nu « extinguished »
 * au milieu de sept libellés français.
 *
 * ## ⚠️ Le piège de nommage, écrit noir sur blanc
 *
 * Il existait **une autre constante `FINS`** dans le code : celle de `screens/Fins/Victoire.tsx`,
 * exportée et importée par `Fin.tsx`. Elle ne décrivait **pas** les raisons de fin : elle décrivait
 * les trois fins de VICTOIRE (`rue`, `ceo`, `communaute`), c'est-à-dire ce que le joueur a
 * choisi de faire du million. Les deux tables étaient des `Record<string, string>` : importer la
 * mauvaise **compilait sans une erreur** et affichait n'importe quoi. ⟦C57 · lot 7⟧ Elle vit
 * désormais plus bas dans ce fichier sous le nom `RECITS_DE_VICTOIRE` : le piège de nommage a disparu.
 *
 * D'où les fonctions plutôt que les tables nues, et surtout leurs noms : `libelleFin` répond à
 * `ended_reason`, `libelleVictoire` répond à `victory_ending`. Le type ne peut pas nous
 * rattraper, le nom doit s'en charger.
 *
 * Aucune règle de jeu ici — c'est une traduction, et rien d'autre.
 */

/**
 * Les `ended_reason` que le jeu POSE encore, en français ⟦C60 · A-177⟧ : la victoire refermée, le
 * burn-out, la mort en légende, l'extinction (le Fantôme) et l'abandon.
 *
 * `abandoned` en fait partie : c'est la valeur du joueur qui repart de zéro de lui-même ⟦A-97⟧. La
 * ranger avec les morts mentirait sur ce qui s'est passé : d'où la porte, pas la pierre tombale.
 *
 * Les trois autres raisons de la base (`reputation`, `ban`, `bankruptcy`) ne naissent plus : le grand
 * ménage a coupé la faillite, les strikes et le bad buzz comme sanction. Elles vivent à part, dans
 * `FINS_ANCIENNES`, pour qu'une partie d'AVANT reste lisible à l'Historique et au Palmarès.
 */
export const FINS: Record<string, string> = {
  victory_closed: "🏁 Le million, chaîne refermée",
  burnout: "🫠 Burn-out terminal",
  legend_death: "⭐ Mort en légende",
  // ⟦A-58⟧ La cinquième fin. Elle manquait, et le palmarès affichait le mot anglais nu
  // « extinguished » au milieu de sept libellés français, dans une interface qui doit être en
  // français (CLAUDE.md). Une table de correspondance ne prévient jamais qu'il lui manque une
  // entrée : elle rend la clé brute, et ça passe pour un texte.
  extinguished: "👻 Éteinte, sans que personne ne le remarque",
  abandoned: "🚪 Abandonnée",
};

/**
 * ⟦C60 · A-177⟧ **Les fins d'avant le grand ménage** : plus aucune nouvelle partie ne se termine
 * ainsi, mais une partie déjà jouée (compte ou bac à sable) peut encore porter l'une de ces trois
 * raisons, et son libellé doit rester lisible plutôt que de tomber en clé brute.
 *
 * Le jour où on rallume le système (`content/meta.json → systemes`), l'entrée est déjà là.
 */
export const FINS_ANCIENNES: Record<string, string> = {
  reputation: "📉 Spirale de réputation",
  ban: "🚫 Chaîne supprimée (3 strikes)",
  bankruptcy: "🏚 Faillite",
};

/** Les trois fins de victoire (`victory_ending`) — ce qu'on a fait du million, pas comment on est mort. */
export const ENDINGS: Record<string, string> = {
  rue: "la rue à ton nom",
  ceo: "le CEO",
  communaute: "la communauté",
};

/**
 * La raison de fin d'une run, en français.
 *
 * La clé brute sert de repli **volontairement** : une `ended_reason` que cette table ne
 * connaîtrait pas doit se voir à l'écran (c'est comme ça qu'⟦A-58⟧ a été trouvé), pas
 * disparaître derrière un « Terminée » qui ne dit rien. `null` ⇒ `null` : une run en cours n'a
 * pas de raison de fin, et lui en inventer une serait pire que de n'en montrer aucune.
 */
export function libelleFin(endedReason: string | null | undefined): string | null {
  if (!endedReason) return null;
  return FINS[endedReason] ?? FINS_ANCIENNES[endedReason] ?? endedReason;
}

/** La fin de victoire (`victory_ending`), en français. Même règle de repli. */
export function libelleVictoire(ending: string | null | undefined): string | null {
  if (!ending) return null;
  return ENDINGS[ending] ?? ending;
}

// ─────────────────────────────────────────────────────────────────────────────
// ⟦C57 · lot 7 · A-174⟧ Crises, chutes et fins : les textes, à UN seul endroit
// ─────────────────────────────────────────────────────────────────────────────
//
// Matt, 02/10/2026 : « Il y a trop de trucs écrits partout, on ne lit plus rien à la fin. »
// Et sur les fins, la décision du même jour : **la narration et l'épitaphe RESTENT à l'écran,
// c'est la récompense** ; seules les EXPLICATIONS (le score, la reprise au palier, l'assurance
// burn-out, ce que font les deux suites du million) passent derrière « Les dessous ». Les crises
// et les chutes au dernier palier, elles, suivent la règle normale : un titre, des chiffres, UNE phrase de
// 12 mots au plus.
//
// ## Pourquoi ces textes vivent ici
//
// Ils vivaient en constantes privées dans quatre écrans (`Fin.tsx`, `Victoire.tsx`, `Crise.tsx`,
// `ChuteAuJalon.tsx`) : le Journal, qui doit garder l'épitaphe, n'avait aucun moyen de les lire sans
// importer un composant React. Même raison d'être que le vocabulaire des fins plus haut : un seul
// endroit, les écrans et le Journal le lisent.
//
// **Aucun texte long n'est réécrit.** Chaque récit garde son `texte` mot pour mot (verrouillé par
// `fins.test.ts`) ; le texte COURT s'écrit à côté (`court`, `phrase`...), jamais à la place. Ces
// textes ne sont pas dans `content/` parce qu'ils n'y ont jamais été : ce sont des textes d'écran,
// écrits dans le code, et les déplacer vers du JSON serait un chantier de contenu à part (schéma,
// boot, serveur) pour une douzaine de phrases. Le texte court est donc écrit à côté du long, ici.
//
// ⚠️ Pas d'import de valeur depuis `journal.ts` : `journal.ts` importe ce fichier pour écrire ses
// lignes de crise, de fin et de victoire. Un import de valeur dans l'autre sens ferait un cycle
// (le type `BlocSource`, lui, s'efface à la compilation).

import {
  BURNOUT_FENETRE_H,
  H_JOUR,
  PALIERS_POST_1M,
  PC_PAR_PALIER,
  type EndingId,
  type GameOverEtendu,
  type Systemes,
} from "@maxime/shared";

import { nombre, nombreCourt, points } from "./format";
import type { BlocSource } from "./journal";

// ── Les fins de partie (game over) ─────────────────────────────────────────────────────

/** Un récit de fin de partie : l'icône, le titre, l'épitaphe COURTE (le Journal) et la narration entière (l'écran). */
export interface RecitDeFin {
  icone: string;
  titre: string;
  /**
   * L'épitaphe courte : UNE phrase. Le Journal écrit `titre : court` sur sa ligne (12 mots au plus,
   * verrouillé par les tests) et garde la narration (`texte`) derrière « Les dessous » de l'entrée.
   * À l'écran de fin, c'est la narration qui reste (décision de Matt, 02/10).
   */
  court: string;
  /** La narration, mot pour mot (cf. `fins.test.ts`). Elle RESTE à l'écran de fin. */
  texte: string;
}

/**
 * ⟦C60 · A-177⟧ Les trois game over que le grand ménage ne pose plus : la spirale de réputation (le
 * bad buzz n'est plus une sanction), le bannissement (plus de strikes) et la faillite (plus de loyer,
 * plus de charges). Le moteur garde leurs identifiants (le type `GameOverEtendu` ne bouge pas, une
 * partie d'avant les porte encore) ; l'écran n'a plus à les raconter que pour la relire.
 */
export type GameOverRetire = "spirale_reputation" | "bannissement" | "faillite";

/** Les game over qui peuvent encore tomber : le burn-out terminal et le Fantôme. */
export type GameOverVivant = Exclude<GameOverEtendu, GameOverRetire>;

/**
 * Les deux game over qui restent, racontés. `texte` est celui que `Fin.tsx` portait dans sa table
 * `CAUSES`, sans une virgule de changée.
 */
export const RECITS_DE_FIN_VIVANTS: Record<GameOverVivant, RecitDeFin> = {
  burnout_terminal: {
    icone: "🫠",
    titre: "Tu t'es éteint",
    court: "Deux crises, et la deuxième n'est pas passée.",
    texte:
      "Deux crises, et la deuxième n'est pas passée. La caméra est restée dans son carton. Ce n'est pas une défaite : c'est ce qui arrive quand on ne s'arrête jamais.",
  },
  // ⟦A-58⟧ §9.4bis : le Fantôme a **son propre écran** (`Fantome.tsx`, trois récits selon l'état) ;
  // cette entrée sert au Journal, à la « mort en légende » et à l'Historique.
  fantome: {
    icone: "👻",
    titre: "La chaîne s'éteint doucement",
    court: "Le silence a fait le reste.",
    texte: "Plus personne en face. Pas de drame, pas d'annonce : le silence a fait le reste.",
  },
};

/**
 * ⟦C60 · A-177⟧ **Les récits d'avant le grand ménage**, gardés mot pour mot pour relire une partie
 * d'avant (une chaîne de bac à sable déjà au `game_over`, un mémorial) et pour le jour où le système
 * se rallume. Aucune nouvelle partie n'y mène : `gameOversPossibles` le dit, et un test le tient.
 */
export const RECITS_DE_FIN_ANCIENS: Record<GameOverRetire, RecitDeFin> = {
  spirale_reputation: {
    icone: "📉",
    titre: "La spirale",
    court: "Plus personne n'écoutait la version longue.",
    texte:
      "La réputation est descendue plus vite que tu ne pouvais l'expliquer. À un moment, plus personne n'écoutait la version longue.",
  },
  bannissement: {
    icone: "⛔",
    titre: "Chaîne supprimée",
    court: "Trois strikes, et la chaîne n'existe plus.",
    texte: "Trois strikes. MeTube ne fait pas de sentiment : la chaîne n'existe plus.",
  },
  faillite: {
    icone: "🏦",
    titre: "Plus un Click",
    court: "Un mois dans le rouge, le matériel est parti.",
    texte:
      "Un mois dans le rouge. Le matériel part, l'appartement aussi. On n'avait pas prévu que ça coûterait autant.",
  },
};

/**
 * Les cinq game over du moteur (`GameOverEtendu`), racontés : les deux qui restent, puis ceux d'avant
 * le ménage. Le type est exhaustif exprès : une sixième fin qui naît ne compile pas sans son récit.
 */
export const RECITS_DE_FIN: Record<GameOverEtendu, RecitDeFin> = {
  ...RECITS_DE_FIN_VIVANTS,
  ...RECITS_DE_FIN_ANCIENS,
};

/**
 * ⟦C60 · A-177⟧ **Les game over qu'une NOUVELLE partie peut encore connaître**, selon les
 * interrupteurs du contenu : le burn-out terminal et le Fantôme toujours, et chacun des trois autres
 * seulement si son système est rallumé (faillite, strikes, bad buzz comme sanction).
 *
 * C'est l'unique lecteur côté client : l'Historique et le Palmarès n'ont aucune cause de faillite à
 * afficher pour une partie neuve tant que cette liste ne la contient pas, et le test le vérifie sur
 * le vrai contenu. Pur : les interrupteurs entrent en paramètre.
 */
export function gameOversPossibles(sys: Systemes): GameOverEtendu[] {
  const possibles: GameOverEtendu[] = ["burnout_terminal"];
  if (sys.bad_buzz) possibles.push("spirale_reputation");
  if (sys.strikes) possibles.push("bannissement");
  if (sys.faillite) possibles.push("faillite");
  possibles.push("fantome");
  return possibles;
}

/**
 * La mort EN LÉGENDE ⟦PLAN §4.13⟧ : mourir après le million ne reprend rien. Le récit de la cause
 * est cité, puis la phrase qui dit que la plaque reste, la même que `Fin.tsx` portait en dur.
 */
export const SUITE_MORT_EN_LEGENDE =
  "Rien de tout ça ne reprend la plaque : elle a été gravée au million, et elle y reste.";

export const MORT_EN_LEGENDE_COURT = "Mort en légende : la plaque reste gravée au million.";

/** Le texte entier de la mort en légende : le récit de la cause (ou un repli), puis la plaque. */
export function texteMortEnLegende(cause: GameOverEtendu | null | undefined): string {
  const recit = cause ? RECITS_DE_FIN[cause]?.texte : undefined;
  return `${recit ?? "La chaîne s'est arrêtée après coup."} ${SUITE_MORT_EN_LEGENDE}`;
}

/** La chaîne refermée sur son million (`clore_run`) : « une fin choisie ». Mot pour mot, comme `Fin.tsx`. */
export const TEXTE_CHAINE_REFERMEE =
  "Tu as refermé la chaîne toi-même, sur sa plus belle page. C'est une fin choisie, pas une fin subie — la seule du jeu.";

export const CHAINE_REFERMEE_COURT = "Refermée sur sa plus belle page, par choix.";

/** Les trois fins de victoire (PLAN §4.13), telles qu'on les nomme au joueur. Texte mot pour mot (`Victoire.tsx` les lisait ici avant). */
export const RECITS_DE_VICTOIRE: Record<EndingId, { titre: string; texte: string }> = {
  la_rue: {
    titre: "La Rue",
    texte:
      "Castelclic a sorti les barrières et le maire a mis son écharpe. On dévoile une plaque émaillée, et il y a plus de monde que le jour de la fête du village.",
  },
  ceo: {
    titre: "CEO",
    texte:
      "Tu ne tournes plus seul depuis longtemps. Il y a un bureau, des gens qui y viennent le matin, et ton nom sur la porte.",
  },
  la_communaute: {
    titre: "La Communauté",
    texte:
      "Ce n'est pas une chaîne, c'est un endroit où des gens se retrouvent. Le million, ce sont eux — et ils le savent.",
  },
};

/** Ce que le Journal écrit quand le moteur pose un game over : l'icône, la ligne courte, et ce qui s'ouvre. */
export interface EntreeDeFin {
  icone: string;
  /** Une phrase de 12 mots au plus (`titre : court`). */
  texte: string;
  /** Le titre du dialogue « Les dessous » de l'entrée. */
  titreDetail: string;
  /** Le détail : la narration entière, intacte. */
  blocs: BlocSource[];
}

/**
 * Le game over du moteur (`cause`, `legende`) en entrée de Journal : l'épitaphe courte sur la ligne,
 * la narration entière derrière « Les dessous ». Une cause que la table ne connaît pas rend `null` :
 * l'appelant garde alors sa ligne d'avant (la cause brute, qui se voit plutôt que de disparaître).
 */
export function entreeDeFin(cause: string | null | undefined, legende: boolean): EntreeDeFin | null {
  const recit = cause && cause in RECITS_DE_FIN ? RECITS_DE_FIN[cause as GameOverEtendu] : null;
  if (legende) {
    return {
      icone: "⭐",
      texte: MORT_EN_LEGENDE_COURT,
      titreDetail: "Mort en légende",
      blocs: [{ paragraphes: [texteMortEnLegende((recit ? cause : null) as GameOverEtendu | null)] }],
    };
  }
  if (!recit) return null;
  return {
    icone: recit.icone,
    texte: `${recit.titre} : ${recit.court}`,
    titreDetail: recit.titre,
    blocs: [{ paragraphes: [recit.texte] }],
  };
}

/**
 * ⟦L'Historique⟧ **L'épitaphe d'une fin, lue par sa `ended_reason`** (le vocabulaire de la base,
 * celui de `libelleFin`), pas par le `GameOverEtendu` du moteur. C'est la porte que l'Historique
 * (et le Palmarès) peuvent ouvrir pour garder la narration derrière « Les dessous » au lieu de
 * l'ignorer : `null` quand il n'y en a pas (une chaîne abandonnée n'a pas d'épitaphe, et lui en
 * inventer une serait pire).
 *
 * Même table que `RAISON_PAR_GAME_OVER` de `historiqueLocal.ts` (ses jumeaux client et serveur
 * se tiennent) ; ici, dans l'autre sens.
 */
export function epitapheDeFin(
  endedReason: string | null | undefined,
): { icone: string; titre: string; court: string; texte: string } | null {
  const parRaison: Record<string, GameOverEtendu> = {
    burnout: "burnout_terminal",
    reputation: "spirale_reputation",
    ban: "bannissement",
    bankruptcy: "faillite",
    extinguished: "fantome",
  };
  if (!endedReason) return null;
  const cause = parRaison[endedReason];
  if (cause) return RECITS_DE_FIN[cause];
  if (endedReason === "victory_closed") {
    return { icone: "📜", titre: "Chaîne refermée", court: CHAINE_REFERMEE_COURT, texte: TEXTE_CHAINE_REFERMEE };
  }
  if (endedReason === "legend_death") {
    return {
      icone: "⭐",
      titre: "Mort en légende",
      court: MORT_EN_LEGENDE_COURT,
      texte: texteMortEnLegende(null),
    };
  }
  return null;
}

// ── Le million : les deux suites, le score ─────────────────────────────────────────────

/**
 * Ce que dit le dialogue « Continuer après la rue ? » : UNE phrase courte. Le texte entier (les
 * paliers, le plafond de l'Atelier) s'ouvre par « Les dessous » de l'écran de victoire.
 */
export const PHRASE_CONTINUER = "Victoire gravée : la chaîne repart vers les paliers suivants.";

/** Ce que dit le dialogue « Refermer la chaîne ici ? » : UNE phrase courte, la conséquence qui compte. */
export function phraseClore(nomDeChaine: string): string {
  return `« ${nomDeChaine} » s'arrête ici, tu ne la rejoueras plus.`;
}

/** Le corps d'origine du dialogue « Continuer après la rue ? », mot pour mot. Il vit maintenant dans les dessous. */
export function texteContinuerApresLaRue(noeudsTotal: number): string {
  return `La victoire reste gravée quoi qu'il arrive. La chaîne repart pour les paliers ${PALIERS_POST_1M.map(nombreCourt).join(" · ")} (+${points(PC_PAR_PALIER)} chacun) avec un seul objectif : les ${noeudsTotal} nœuds de l'arbre. L'Atelier reste au plafond d'exigence — c'est une quête d'endurance, pas une victoire à refaire.`;
}

/** Le corps d'origine du dialogue « Refermer la chaîne ici ? », mot pour mot. Il vit maintenant dans les dessous. */
export function texteClore(nomDeChaine: string, noeudsRestants: number): string {
  return `« ${nomDeChaine} » s'arrête sur sa plus belle page. Ton score et ta plaque sont gravés — plus rien ne peut les reprendre — mais tu ne joueras plus cette run. Il te reste ${noeudsRestants} nœud(s) d'arbre non pris.`;
}

/** Quand le moteur ne nomme pas encore la fin : l'écran dit seulement que la victoire est gravée. */
export const PHRASE_FIN_NON_NOMMEE = "Ta victoire est gravée.";

/** Le texte d'origine de ce même cas, mot pour mot : il vit dans les dessous. */
export const TEXTE_FIN_NON_NOMMEE =
  "La rue, tu l'as. Ce qui reste à départager, c'est La Rue, CEO ou La Communauté : le moteur ne tranche pas encore laquelle est la tienne. Ta victoire, elle, est gravée.";

/** Le score absent (bac à sable), mot pour mot : il vit dans les dessous. */
export const TEXTE_SCORE_ABSENT =
  "Le score sur 1 000 points (vitesse, fidélité, argent) se grave côté serveur : le bac à sable ne le calcule pas.";

/**
 * ⟦A-74⟧ Les ingrédients du score, dans les mots de PLAN §4.13, jamais les ids. La divergence
 * « bad buzz survécus compte les crises de SM » est un arbitrage de Mathieu (`server/src/progression.ts`
 * la signale) : l'écran affiche le libellé promis et le nombre transporté, il n'invente ni ne corrige.
 *
 * ⟦C60 · A-177⟧ Un score gravé aujourd'hui n'a que trois lignes : vitesse, fidélité, argent (les
 * jalons et le bad buzz ne comptent plus, le serveur partage les 1 000 points entre les trois qui
 * restent). `jalons` et `bad_buzz` ne servent qu'à relire un score d'avant : l'écran itère les lignes
 * que le serveur envoie, jamais cette table.
 */
export const NOMS_SCORE: Record<string, string> = {
  vitesse: "Vitesse",
  fidelite: "Fidélité",
  jalons: "Jalons",
  bad_buzz: "Bad buzz survécus",
  argent: "Argent",
};

// ── Les crises de santé mentale (SPEC-UI §7.5) ─────────────────────────────────────────

/** Les trois visages d'une crise : la première (on est prévenu), le dernier avertissement, la terminale. */
export type VarianteCrise = "normale" | "dernier_avertissement" | "terminale";

export function varianteDeCrise(crise: { terminal: boolean; dernier_avertissement: boolean }): VarianteCrise {
  return crise.terminal ? "terminale" : crise.dernier_avertissement ? "dernier_avertissement" : "normale";
}

/**
 * La phrase UNIQUE que l'écran de crise garde (12 mots au plus). Le reste de ce qu'il disait
 * (`suiteDeCrise`) passe derrière « Les dessous ».
 */
export const PHRASE_DE_CRISE: Record<VarianteCrise, string> = {
  normale: "Tu remontes la pente, doucement.",
  dernier_avertissement: "Dernier avertissement : une autre crise sans pause, et c'est fini.",
  terminale: "Ce n'est plus une crise : c'est la fin d'un cycle.",
};

/** La fenêtre de la crise terminale, en jours (90) : lue sur le moteur, jamais recopiée. */
export function joursDeFenetre(): number {
  return Math.round(BURNOUT_FENETRE_H / H_JOUR);
}

/**
 * Le premier paragraphe de l'écran de crise, mot pour mot, mais SANS le nom de la chaîne quand on
 * ne l'a pas (le Journal : un fait du moteur ne connaît pas le nom de la run).
 */
export function introDeCrise(jours: number, nomDeChaine?: string): string {
  return `La santé mentale est tombée à zéro. Tu t'es arrêté net, et pendant ${nombre(jours)} jours ${
    nomDeChaine ? `« ${nomDeChaine} »` : "la chaîne"
  } a tourné sans toi — mal.`;
}

/**
 * Ce que l'écran de crise disait en dessous du bilan, mot pour mot : le texte long de la variante.
 * `accroche` est la phrase en gras du dernier avertissement (elle n'existe que là).
 */
export function suiteDeCrise(
  variante: VarianteCrise,
  joursFenetre: number = joursDeFenetre(),
): { accroche?: string; texte: string } {
  if (variante === "terminale") {
    return {
      texte: `Deux crises en moins de ${joursFenetre} jours, sans pause ni thérapie entre les deux : celle-ci est la bonne. Ce qui suit n'est plus une crise, c'est la fin d'un cycle.`,
    };
  }
  if (variante === "dernier_avertissement") {
    return {
      accroche: "Dernier avertissement.",
      texte: `Une deuxième crise dans les ${joursFenetre} jours, sans pause ni thérapie entre les deux, et tu t'arrêtes pour de bon. Dors. Ou va souffler — pour de vrai.`,
    };
  }
  return {
    texte: `Tu remontes la pente. Doucement : une pause ou une thérapie remet le compteur des crises à zéro — une deuxième dans les ${joursFenetre} jours sans remède ne pardonne pas.`,
  };
}

/**
 * L'assurance burn-out, dite en une ligne : la part de la perte que la rente des anciennes vidéos a
 * retenue. ⟦C60 · A-177⟧ Les vieilles vidéos continuent de rapporter sans écran de gestion : on dit
 * « tes anciennes vidéos », le mot de la bibliothèque n'est plus à l'écran.
 */
export function ligneAssuranceBurnout(renteDeduite: number): string {
  return `La rente de tes anciennes vidéos en a retenu ${nombre(renteDeduite)} : c'est l'assurance burn-out.`;
}

/**
 * Le détail d'une crise, en blocs prêts pour `detailJournal` : le récit, l'assurance burn-out (si la
 * rente a retenu quelque chose) et ce qui attend après. UN seul assemblage, donc la même lecture à
 * l'écran de crise (« Les dessous ») et au Journal. Les chiffres (jours, abonnés perdus, santé
 * mentale remontée) restent sur la ligne et sur l'écran, pas ici.
 */
export function blocsDeCrise(p: {
  jours: number;
  rente_deduite: number;
  variante: VarianteCrise;
  nomDeChaine?: string;
}): (BlocSource | null)[] {
  const suite = suiteDeCrise(p.variante);
  return [
    { paragraphes: [introDeCrise(p.jours, p.nomDeChaine)] },
    p.rente_deduite > 0
      ? { titre: "L'assurance burn-out", lignes: [{ icone: "🌲", texte: ligneAssuranceBurnout(p.rente_deduite) }] }
      : null,
    { titre: "Et maintenant", paragraphes: [[suite.accroche, suite.texte].filter(Boolean).join(" ")] },
  ];
}

// ── La chute : le retour au dernier palier (SPEC-API §7.3) ─────────────────────────────

/**
 * La phrase UNIQUE que l'écran de chute garde sous le bilan (12 mots au plus) : où l'on repart, et
 * ce que ça a coûté en jours. La reprise entière (« aucune compétence n'est reprise »...) passe
 * derrière « Les dessous ».
 *
 * ⟦C60 · A-177⟧ On dit « le palier », plus « le jalon » : les vidéos jalons n'existent plus, et c'est
 * bien le dernier palier d'abonnés franchi qu'on retrouve.
 */
export function phraseDeReprise(retour: { palier: number | null; jour_avant: number; jour_apres: number }): string {
  if (retour.palier === null) return "Tu repars de zéro, mais tu sais déjà comment on fait.";
  const jours = retour.jour_avant - retour.jour_apres;
  return `Tu repars du palier des ${nombre(retour.palier)} abonnés${jours > 0 ? ` : ${nombre(jours)} ${jours > 1 ? "jours effacés" : "jour effacé"}` : ""}.`;
}

/**
 * La reprise au dernier palier, en entier : ce que l'écran de chute disait sous le bilan (les
 * passages en gras sont du texte courant dans le dialogue). ⟦C60 · A-177⟧ « jalon » est devenu
 * « palier », et les tirets cadratins ont cédé la place à une virgule et à une parenthèse.
 */
export function texteDeReprise(
  nomDeChaine: string,
  retour: { palier: number | null; jour_avant: number; jour_apres: number },
): string {
  if (retour.palier === null) {
    return `Tu n'avais pas encore de palier derrière toi : « ${nomDeChaine} » repart de zéro. Tu sais déjà comment on fait, c'est toute la différence avec la première fois.`;
  }
  const jours = retour.jour_avant - retour.jour_apres;
  return `Tu repars du palier des ${nombre(retour.palier)} abonnés${
    jours > 0 ? ` (${jours} jours de travail effacés)` : ""
  }. Tout ce que tu as appris à l'arbre, tu le gardes : aucune compétence n'est reprise.`;
}

/**
 * ⟦C60 · A-177⟧ Ce que la reprise fait des Clicks, pour « Les dessous » de la chute : on repart avec la
 * moitié de ce que le palier avait en caisse (`REPRISE_MONEY_RATIO`, le moteur). `argentApres` vient du
 * bilan du serveur (jamais recalculé ici).
 *
 * ⟦C60 bis · A-177⟧ **La chute ne retire plus de Clicks** (Matt, 03/10/2026 : « ne plus rien retirer »). La part
 * que la reprise garde est un réglage du contenu (`systemes.reprise_clicks`, 0,5 avant, 1 livré) : à 1, il n'y a
 * rien à raconter, et l'écran ne parle plus de Clicks du tout (`null`). Moins de 1 (le jeu d'avant), la phrase
 * d'avant, mot pour mot pour la moitié.
 */
export function texteClicksDeReprise(argentApres: number, partGardee: number = 0.5): string | null {
  if (partGardee >= 1) return null;
  const part = partGardee === 0.5 ? "la moitié" : `${nombre(Math.round(partGardee * 100))} %`;
  return `Tu repars avec ${nombre(argentApres)} ₵ : ${part} de ce que tu avais en caisse à ce palier.`;
}
